import React, { useState, useEffect, useMemo } from "react";
import "./StatementView.css";
import { useDispatch, useSelector } from "react-redux";
import { DocumentViewer } from "../../DocumentViewer/DocumentViewer";
import { resetSelectedInvoiceData, setSelectedInvoiceData } from "../../../Reducer/SelectedInvoiceDataReducer";
import StatementForm from "../StatementForm/StatementForm";
import StatementHeader from "../StatementHeader/StatementHeader";
import { GetInvoiceService } from "../../../Services/InvoiceService/InvoiceService";
import { parseDocumentUrls } from "../../DocumentViewer/utils";
import Loader from "../../Loader/Loader";
import { SupplierStatementArchiveRecord } from "../../../Types/SupplierStatementArchive";
import { useUnsavedChangesGuard } from "../UnsavedChangesGuard";
import AlertMessage from "../../AlertMessage/AlertMessage";

interface StatementViewProps {
  setStatementView: React.Dispatch<React.SetStateAction<boolean>>;
  refreshInvoices?: () => void;
  initialStatement?: SupplierStatementArchiveRecord;
  historyEntityType?: "Invoice" | "SupplierStatement";
  navigation?: {
    currentIndex: number;
    totalCount: number;
    onPrevious: () => void;
    onNext: () => boolean;
  };
}

const StatementView: React.FC<StatementViewProps> = ({
  setStatementView,
  refreshInvoices,
  initialStatement,
  historyEntityType = "Invoice",
  navigation,
}) => {
  const dispatch = useDispatch();
  const selectedInvoice = useSelector((state: any) => state.selectedInvoice);
  const selectedClient = useSelector((state: any) => state.selectedClient.selectedClient);
  const selectedInvoiceData = useSelector(
    (state: any) => state.selectedInvoiceData?.selectedInvoiceData,
  );
  const [isPreviewActive, setIsPreviewActive] = useState(true);
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [mistakeReported, setMistakeReported] = useState(false);
  const { guardNavigation, setHasUnsavedChanges } = useUnsavedChangesGuard();
  const invoiceId = initialStatement?.invoiceId ??
    (Number(selectedInvoice?.selectedInvoice?.id) > 0
      ? Number(selectedInvoice.selectedInvoice.id)
      : Number(selectedInvoiceData?.invoiceId) > 0
        ? Number(selectedInvoiceData.invoiceId)
        : null);
  const clientId = String(
    selectedClient?.clientId ?? selectedClient?.userId ?? selectedClient?.id ?? "",
  );
  const urls = useMemo(() => parseDocumentUrls(documentUrl), [documentUrl]);

  useEffect(() => {
    let cancelled = false;

    if (initialStatement) {
      setMistakeReported(false);
      setLoading(true);
      GetInvoiceService(initialStatement.invoiceId)
        .then((invoice) => {
          if (cancelled) return;
          const statementData = {
            ...initialStatement,
            ...invoice,
            invoiceId: initialStatement.invoiceId,
            userId: clientId,
            documentOwner: initialStatement.user,
            invoiceDate: initialStatement.date,
            statementPeriod: initialStatement.date,
            description: initialStatement.note,
            totalAmount: initialStatement.outstandingBalance,
            outstandingBalance: initialStatement.outstandingBalance,
            amount: initialStatement.outstandingBalance,
            reconciliationItems: invoice?.reconciliationItems ?? [],
          };
          setDocumentUrl(invoice?.documentUrl ?? initialStatement.documentUrl);
          dispatch(setSelectedInvoiceData(statementData));
        })
        .catch((err: unknown) =>
          console.error("Failed to load supplier statement details:", err),
        )
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    } else {
      const id = selectedInvoice?.selectedInvoice?.id;
      if (!id) return () => {
        cancelled = true;
      };

      setMistakeReported(false);
      setLoading(true);
      GetInvoiceService(id)
        .then((invoice: any) => {
          if (cancelled) return;
          if (invoice) {
            setDocumentUrl(invoice.documentUrl ?? "");
            dispatch(setSelectedInvoiceData(invoice));
          }
        })
        .catch((err: any) => console.error("Failed to load statement document:", err))
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    dispatch,
    initialStatement,
    invoiceId,
    selectedInvoice?.selectedInvoice?.id,
    setStatementView,
  ]);

  const handleClose = () => {
    setStatementView(false);
    dispatch(resetSelectedInvoiceData());
    refreshInvoices?.();
  };

  const handleStatementActionSuccess = () => {
    if (
      historyEntityType === "SupplierStatement" &&
      navigation?.onNext()
    ) {
      void refreshInvoices?.();
      return;
    }

    handleClose();
  };

  const handleStatementSaved = async () => {
    if (invoiceId === null) return;

    try {
      const refreshedInvoice = await GetInvoiceService(invoiceId);
      if (!refreshedInvoice) {
        throw new Error("The updated supplier statement could not be reloaded.");
      }

      dispatch(
        setSelectedInvoiceData({
          ...selectedInvoiceData,
          ...refreshedInvoice,
        }),
      );
      setDocumentUrl(refreshedInvoice.documentUrl ?? "");
      await refreshInvoices?.();
    } catch (error) {
      console.error("Failed to refresh the saved supplier statement:", error);
      AlertMessage({
        type: "warning",
        text: "Statement saved, but the view could not be refreshed. Reopen it to see the latest data.",
      });
    }
  };

  return (
    <div className="statementView">
      <StatementHeader
        onClose={handleClose}
        mistakeReported={mistakeReported}
        onMistakeReportedChange={setMistakeReported}
        invoiceId={invoiceId}
        onReconciled={handleStatementActionSuccess}
        onBeforeAction={guardNavigation}
        archiveRecord={initialStatement}
        navigation={navigation}
      />

      <div className="statementView-body">
        <div className="statementView-body-left" onMouseEnter={() => setIsPreviewActive(true)}>
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Loader />
            </div>
          ) : (
            <DocumentViewer urls={urls} isToolbarActive={isPreviewActive} />
          )}
        </div>
        <div className="statementView-body-right" onMouseEnter={() => setIsPreviewActive(false)}>
          <StatementForm
            mistakeReported={mistakeReported}
            historyEntityType={historyEntityType}
            onDirtyChange={setHasUnsavedChanges}
            onSaved={() => void handleStatementSaved()}
          />
        </div>
      </div>
    </div>
  );
};

export default StatementView;
